import { normalizeReports } from '../report-data';
import { supabase, unwrap } from '../supabase';
import { resolveRangeBounded, viewerTimeZone, type DateRangeValue } from '../dates';
import type { Overview, Reports } from '../types';

const n = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const s = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));
const list = (v: unknown) => (Array.isArray(v) ? (v as Record<string, unknown>[]) : []);

function rangeArgs(orgId: string, range: DateRangeValue, employeeId?: string) {
  const { start, end } = resolveRangeBounded(range);
  return {
    p_org_id: orgId,
    p_start_date: start.toISOString(),
    p_end_date: end.toISOString(),
    p_employee_id: employeeId ? Number(employeeId) : null,
    p_time_zone: viewerTimeZone(),
  };
}

export async function getOverview(
  orgId: string,
  range: DateRangeValue,
  employeeId?: string,
): Promise<Overview> {
  const data = (unwrap(await supabase.rpc('get_dashboard_stats', rangeArgs(orgId, range, employeeId))) ??
    {}) as Record<string, unknown>;
  const m = (data.metrics ?? {}) as Record<string, unknown>;
  const uniqueCallers = n(m.total_persons);
  const connectedCallers = n(m.total_connected_persons ?? m.connected_persons);

  return {
    metrics: {
      totalCalls: n(m.total_calls),
      connected: n(m.connected),
      notAnswered: n(m.not_answered_calls),
      uniqueCallers,
      connectedCallers,
      neverConnectedCallers: Math.max(0, uniqueCallers - connectedCallers),
      unreturnedMissed: n(m.unreturned_missed),
      totalDuration: n(m.total_duration),
      incomingDuration: n(m.incoming_duration),
      outgoingDuration: n(m.outgoing_duration),
    },
    daily: list(data.breakdown)
      .map((d) => ({
        date: String(d.date ?? ''),
        totalCalls: n(d.total_calls),
        connected: n(d.connected),
        missed: n(d.missed),
        totalDuration: n(d.total_duration),
      }))
      .filter((d) => d.date)
      .sort((a, b) => a.date.localeCompare(b.date)),
    topCallers: list(data.topCallers).map((c) => ({
      phone: String(c.phone ?? ''),
      name: s(c.name),
      calls: n(c.count ?? c.calls),
      duration: n(c.duration),
    })),
    longestCallers: list(data.mostTimeSpent).map((c) => ({
      phone: String(c.phone ?? ''),
      name: s(c.name),
      calls: n(c.callCount ?? c.count),
      duration: n(c.duration),
    })),
    employees: list(data.employeeStats).map((e) => ({
      id: e.id === null || e.id === undefined ? null : n(e.id),
      name: String(e.name ?? 'Unknown'),
      totalCalls: n(e.total_calls),
      connected: n(e.connected),
      notConnected: n(e.not_connected),
      totalDuration: n(e.total_duration),
    })),
  };
}

export async function getReports(
  orgId: string,
  range: DateRangeValue,
  employeeId?: string,
): Promise<Reports> {
  const data = (unwrap(await supabase.rpc('get_reports_data', rangeArgs(orgId, range, employeeId))) ??
    {}) as Record<string, unknown>;
  return normalizeReports(data);
}
